<template>
  <FormSection>
    <Card>
      <ElForm
        ref="accountInformation"
        autocomplete="off"
        :model="accountInformationModel"
        :rules="rules"
        label-position="top"
        @submit.native.prevent="submitAccountInformationForm"
      >
        <ElFormItem :label="$t('EMAIL')" prop="email">
          <ElInput
            :value="email"
            dir="ltr"
            :placeholder="$t('EMAIL')"
            type="email"
            name="email"
            class="text-latin"
            disabled
          />
        </ElFormItem>

        <ElFormItem :label="$t('PUBLISHER_ACCOUNT.FULL_NAME')" prop="name">
          <ElInput
            v-model="accountInformationModel.name"
            dir="rtl"
            name="name"
          />
          <FieldError :errors="errors" field="name" />
          <p class="el-form-item__help">
            <i class="el-icon-warning" />
            {{ $t('AUTH.STEPS.NAME_WARNING') }}
          </p>
        </ElFormItem>

        <ElFormItem
          :label="$t('PUBLISHER_ACCOUNT.NATIONAL_CODE')"
          prop="national_code"
        >
          <ElInput
            v-model="accountInformationModel.national_code"
            dir="ltr"
            name="national_code"
            :maxlength="12"
            @input.native="verifyNationalCode"
          />
          <FieldError field="national_code" :errors="errors" />
          <p class="el-form-item__help">
            <i class="el-icon-warning" />
            {{ $t('PUBLISHER_ACCOUNT.NATIONAL_CODE_HELP_TEXT') }}
          </p>
          <p
            v-if="nationalCodeIsUsed"
            class="el-form-item__help el-form-item__help--warning"
          >
            <i class="el-icon-warning" />
            {{ $t('PUBLISHER_ACCOUNT.NATIONAL_CODE_USED_WARNING') }}
          </p>
        </ElFormItem>

        <ElFormItem :label="$t('MOBILE_PHONE')" prop="phone">
          <ElInput
            v-model="accountInformationModel.phone"
            type="tel"
            dir="ltr"
            name="phone"
            placeholder="09121234567"
            :maxlength="11"
          />

          <FieldError :errors="errors" field="phone" />
          <p class="el-form-item__help">
            <i class="el-icon-warning" />
            {{ $t('AUTH.STEPS.CELLPHONE_WARNING') }}
          </p>
        </ElFormItem>

        <ElFormItem :label="$t('POSTAL_CODE')" prop="postal_code" required>
          <ElInput
            v-model="accountInformationModel.postal_code"
            dir="ltr"
            name="postal_code"
            :maxlength="10"
          />
          <FieldError :errors="errors" field="postal_code" />
        </ElFormItem>

        <ElFormItem :label="$t('PROVINCE')" prop="province" required>
          <ElSelect
            v-model="accountInformationModel.province"
            class="show-for-medium"
            name="province"
            :placeholder="$t('CHOOSE')"
          >
            <ElOption
              v-for="province in provinces"
              :key="province.id"
              :label="getProvinceLabel(province)"
              :value="province.slug"
            />
          </ElSelect>

          <select
            v-model="accountInformationModel.province"
            class="hide-for-medium"
            name="province"
          >
            <option
              v-for="province in provinces"
              :key="province.id"
              :value="province.slug"
            >
              {{ getProvinceLabel(province) }}
            </option>
          </select>
          <FieldError :errors="errors" field="province" />
        </ElFormItem>

        <ElFormItem :label="$t('CITY')" prop="city" required>
          <ElInput v-model="accountInformationModel.city" name="city" />
          <FieldError :errors="errors" field="city" />
        </ElFormItem>

        <ElFormItem :label="$t('ADDRESS')" prop="address">
          <ElInput
            v-model="accountInformationModel.address"
            :autosize="{ minRows: 2, maxRows: 4 }"
            name="address"
            :maxlength="150"
            type="textarea"
          />
          <FieldError :errors="errors" field="address" />
        </ElFormItem>

        <ElFormItem
          :label="$t('PUBLISHER_ACCOUNT.NATIONAL_CARD_IMAGE')"
          prop="national_card_image"
          required
        >
          <Droppable
            :has-preview="true"
            :has-progress="true"
            file-types="image/*"
            :upload-url="uploadPaths.nationalCard"
            @uploaded="updateNationalImage"
          >
            <div slot="label">
              {{ $t('PUBLISHER_ACCOUNT.DRAG_NATIONAL_IMAGE') }}
            </div>
          </Droppable>
          <FieldError :errors="errors" field="national_card_image" />
        </ElFormItem>

        <div class="auth-steps__navigation">
          <ElButton type="default" @click.native="handlePrevPage">
            {{ $t('AUTH.STEPS.PREV_LEVEL_BTN') }}
          </ElButton>
          <ElButton type="primary" native-type="submit" :loading="loading">
            {{ $t('AUTH.STEPS.NEXT_LEVEL_BTN') }}
          </ElButton>
        </div>
      </ElForm>
    </Card>
  </FormSection>
</template>

<script>
import debounce from 'lodash.debounce';
import normalizeNumbers from '@/utils/normalizeNumbers';

import Droppable from '@@/layout/Droppable';

import form from '@/mixins/form';
import lang from '@/mixins/lang';

import provinces from '@/utils/provinces';
import { isValidIranianNationalCode } from '@/utils/validations';

import { Publishers, Register } from '@/api/api';
import FieldError from '@@/common/FieldError';
import FormSection from '@@/common/layout/FormSection';
import Card from '@@/common/Card';

export default {
  name: 'DomesticNatural',

  components: {
    FieldError,
    Droppable,
    FormSection,
    Card,
  },

  mixins: [form, lang],

  props: {
    email: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      nationalCodeIsUsed: false,
      accountInformationModel: {
        name: null,
        national_code: null,
        national_card_image: {
          id: null,
          url: null,
        },

        province: null,
        city: null,
        address: null,
        phone: null,
        postal_code: null,
      },

      uploadPaths: Publishers.uploadPaths,

      provinces,
    };
  },

  computed: {
    rules() {
      const rules = {
        required: true,
        message: this.$t('VALIDATION_REQUIRED_ERR'),
        trigger: 'blur',
      };

      return {
        name: {
          ...rules,
          type: 'string',
        },
        phone: [
          rules,
          {
            pattern: /^09\d{9}$/i,
            message: this.$t('PUBLISHER_ACCOUNT.INVALID_PHONE'),
            trigger: 'blur',
          },
        ],
        postal_code: [
          rules,
          {
            pattern: /^\d{10}$/i,
            message: this.$t('PUBLISHER_ACCOUNT.INVALID_POSTAL_CODE'),
            trigger: 'blur',
          },
        ],
        province: rules,
        city: {
          ...rules,
          type: 'string',
        },
        address: {
          ...rules,
          type: 'string',
        },
        national_card_image: rules,
        national_code: [
          rules,
          { validator: this.validateNationalCode, trigger: 'blur' },
        ],
      };
    },
  },

  watch: {
    accountInformationModel: {
      deep: true,
      handler: normalizeNumbers,
    },
  },

  methods: {
    validateNationalCode(rule, value, callback) {
      if (value.length === 12) {
        callback();
      } else if (!isValidIranianNationalCode(value)) {
        callback(new Error(this.$t('INVALID_INPUT_VALUE_NATIONAL_CODE')));
      } else {
        callback();
      }
    },
    verifyNationalCode() {
      this.nationalCodeIsUsed = false;

      if (!this.verifyByApiCall) {
        this.verifyByApiCall = debounce(() => {
          const { national_code: nc } = this.accountInformationModel;
          if (!nc || nc.length < 10) return;

          // since length becomes 10 only once, we don't need to throttle this call
          return Register.verifyNationalCodeOrId({ national_code: nc })
            .then(({ body: { is_valid: isValid } }) => {
              this.nationalCodeIsUsed = !isValid;
            })
            .catch(() => {
              /* DO Nothing, just to prevent UnhandledRejection error */
            });
        }, 1000);
      }
      this.verifyByApiCall();
    },

    submitAccountInformationForm() {
      if (!this.isValid('accountInformation')) {
        return false;
      }
      this.$emit('submitAccountInformation', this.accountInformationModel);
    },

    handlePrevPage() {
      return this.$emit('prevStep');
    },

    getProvinceLabel(province) {
      if (this.lang !== 'fa') {
        return province.slug;
      }
      return province.title;
    },

    updateNationalImage(src) {
      this.accountInformationModel.national_card_image.id = src;
    },
  },
};
</script>
